/**
 * Laufzeitkonfiguration der Oberfläche.
 *
 * Vite bäckt `import.meta.env` fest ins Bundle. Für ein Container-Image, das
 * unverändert aus der Testumgebung in die Produktion wandern soll, ist das die
 * falsche Reihenfolge: Der Bit-Stand, der geprüft wurde, wäre nicht der, der
 * ausgeliefert wird — und ein Rollback zeigte auf ein Image, dessen Inhalt
 * niemand mehr sicher benennen kann.
 *
 * Deshalb liest die Anwendung ihre Werte zur Laufzeit aus
 * `window.__FLEET_CONFIG__`. Die Datei `config.js`, die dieses Objekt setzt,
 * entsteht beim Start des Containers aus Umgebungsvariablen
 * (`docker/web/entrypoint.sh`) — in Kubernetes üblicherweise aus einer ConfigMap.
 *
 * `import.meta.env` bleibt als Rückfall für `pnpm dev` und für Builds außerhalb
 * eines Containers: Dort gibt es keinen Entrypoint, und die Werte kommen
 * weiterhin aus der `.env` im Wurzelverzeichnis.
 */

export interface RuntimeConfig {
  apiBaseUrl: string;
  entraTenantId: string;
  entraSpaClientId: string;
  entraApiClientId: string;
}

declare global {
  interface Window {
    /** Setzt `config.js` — im Container erzeugt, außerhalb absichtlich leer. */
    __FLEET_CONFIG__?: Partial<RuntimeConfig>;
  }
}

const injected = typeof window === 'undefined' ? undefined : window.__FLEET_CONFIG__;

/**
 * Ein leeres Feld zählt als „nicht gesetzt".
 *
 * Der Entrypoint schreibt für jede fehlende Variable ein leeres Feld, statt es
 * wegzulassen — nur so ist die erzeugte Datei bei jedem Start gleich aufgebaut
 * und beim Debuggen lesbar. Ohne diese Prüfung überschriebe genau dieses leere
 * Feld den Rückfall.
 */
const pick = (runtime: string | undefined, buildTime: string | undefined): string =>
  runtime || buildTime || '';

export const config: RuntimeConfig = {
  apiBaseUrl:
    pick(injected?.apiBaseUrl, import.meta.env.VITE_API_BASE_URL) || 'http://localhost:3000',
  entraTenantId: pick(injected?.entraTenantId, import.meta.env.VITE_ENTRA_TENANT_ID),
  entraSpaClientId: pick(injected?.entraSpaClientId, import.meta.env.VITE_ENTRA_SPA_CLIENT_ID),
  entraApiClientId: pick(injected?.entraApiClientId, import.meta.env.VITE_ENTRA_API_CLIENT_ID),
};
